require("./threepl-service-map.scss");

import {
  ChangeDetectionStrategy, Component, OnChanges, SimpleChanges, computed, inject, input, signal, viewChild,
} from "@angular/core";
import {GoogleMap} from "@angular/google-maps";
import {bzIconFit} from "@bringoz/ui/icons";
import {GeoPoint, ThreePLServiceDetails} from "../../models/threepl-service-details.model";
import {MapsCommonService} from "../../../../map/services/mapsCommon.service";

const toLatLng = (p: GeoPoint): google.maps.LatLngLiteral => ({lat: p.latitude, lng: p.longitude});

export function showDriver(service: ThreePLServiceDetails): boolean {
  return !!service?.threePlExtraData?.driverLocation && service.status !== "COMPLETED";
}

export function serviceMapPoints(service: ThreePLServiceDetails | undefined): google.maps.LatLngLiteral[] {
  if (!service) {
    return [];
  }
  const points = [service.pickup?.address?.location, service.destination?.address?.location]
    .filter((p): p is GeoPoint => !!p)
    .map(toLatLng);
  if (showDriver(service)) {
    points.push(toLatLng(service.threePlExtraData.driverLocation));
  }
  return points;
}

@Component({
  selector: "app-threepl-service-map",
  templateUrl: "./threepl-service-map.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThreeplServiceMapComponent implements OnChanges {
  readonly service = input.required<ThreePLServiceDetails>();

  readonly map = viewChild(GoogleMap);
  readonly fitIcon = bzIconFit;
  readonly satellite = signal(false);

  private readonly maps = inject<MapsCommonService>("mapsCommonService" as never);
  private fittedOnce = false;

  readonly center = this.maps.getSiteLocation();
  readonly zoom = 14;
  readonly mapOptions = computed<google.maps.MapOptions>(() => ({
    styles: this.maps.getDefaultStyle(),
    mapTypeControl: false,
    maxZoom: 18,
    scaleControl: true,
    mapTypeId: this.satellite() ? google.maps.MapTypeId.SATELLITE : google.maps.MapTypeId.ROADMAP,
  }));

  readonly pickupIcon = this.maps.getTaskMarkerIcon("Pickup");
  readonly dropoffIcon = this.maps.getTaskMarkerIcon("Dropoff");
  private agentIcon: google.maps.Icon | undefined;

  readonly pickup = computed(() => this.locationOf(this.service()?.pickup?.address?.location));
  readonly destination = computed(() => this.locationOf(this.service()?.destination?.address?.location));
  readonly driver = computed(() => (showDriver(this.service()) ? toLatLng(this.service().threePlExtraData.driverLocation) : undefined));
  readonly driverOptions = computed<google.maps.MarkerOptions>(() => ({icon: this.agentIcon, visible: true}));

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes["service"];
    if (change?.isFirstChange()) {
      if (this.service()?.threePlExtraData?.driverLocation) {
        this.agentIcon = this.maps.getThreeplServiceAgentIcon();
      }
    } else if (change && this.service()) {
      this.fitBounds();
    }
  }

  onIdle(): void {
    if (!this.fittedOnce) {
      this.fittedOnce = true;
      this.fitBounds();
    }
  }

  fitBounds(): void {
    const points = serviceMapPoints(this.service());
    const map = this.map();
    if (!points.length || !map) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds);
  }

  private locationOf(p: GeoPoint | undefined): google.maps.LatLngLiteral | undefined {
    return p ? toLatLng(p) : undefined;
  }
}
